import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Typography, Card, Button, Table, Select, Form, message, Space, Spin, Row, Col } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useBiathlonEvent } from '../contexts/BiathlonEventContext';
import { useNavigate } from 'react-router-dom';
import { Participant, BiathlonEvent } from '../types/api';
import { fetchBiathlonEvents, getNext30Events, fetchStartlistData, fetchResultData } from '../services/eqTimingApi';

const { Title, Text } = Typography;
const { Option } = Select;

interface RelayTeam {
  id: string;
  name: string;
  club: string;
  members: Participant[];
}

const Stafettuttak: React.FC = () => {
  const { selectedEvent, setSelectedEvent, athletes, loading, clubs, classes, kretser, selectedKretser, setSelectedKretser } = useBiathlonEvent();
  const navigate = useNavigate();
  const [events, setEvents] = useState<BiathlonEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [selectedEvent2, setSelectedEvent2] = useState<BiathlonEvent | null>(null);
  const [selectedClasses2, setSelectedClasses2] = useState<string[]>([]);
  const [athletes2, setAthletes2] = useState<Participant[]>([]);
  const [loading2, setLoading2] = useState(false);
  const [results1, setResults1] = useState<Map<number, Participant>>(new Map());
  const [results2, setResults2] = useState<Map<number, Participant>>(new Map());
  const isInitialLoad = useRef(true);

  // Load persisted selections from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('stafettuttakSelections');
    console.log('localStorage content on mount:', stored);
    
    try {
      if (stored) {
        const selections = JSON.parse(stored);
        console.log('Loading persisted selections:', selections);
        setSelectedClasses(selections.selectedClasses || []);
        setSelectedClasses2(selections.selectedClasses2 || []);
        
        // Load event 2 if it was selected
        if (selections.selectedEvent2Id) {
          // We'll set this after events are loaded
          sessionStorage.setItem('stafettuttak_event2Id', selections.selectedEvent2Id);
        }
      } else {
        console.log('No persisted selections found in localStorage');
      }
    } catch (error) {
      console.error('Error loading persisted selections:', error);
    }
    
    // Mark initial load as complete after a short delay
    setTimeout(() => {
      isInitialLoad.current = false;
    }, 100);
  }, []);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setEventsLoading(true);
        const allEvents = await fetchBiathlonEvents();
        const next30Events = getNext30Events(allEvents);
        setEvents(next30Events);
        
        // Restore event 2 selection if it was persisted
        const event2Id = sessionStorage.getItem('stafettuttak_event2Id');
        if (event2Id) {
          const event = next30Events.find(e => e.Id.toString() === event2Id);
          if (event) {
            setSelectedEvent2(event);
          }
          sessionStorage.removeItem('stafettuttak_event2Id');
        }
      } catch (err) {
        console.error('Failed to load biathlon events', err);
      } finally {
        setEventsLoading(false);
      }
    };

    loadEvents();
  }, []);

  // Load results for event 1
  useEffect(() => {
    const loadResults1 = async () => {
      console.log('🔵 loadResults1 useEffect triggered');
      console.log('  selectedEvent:', selectedEvent);
      console.log('  athletes.length:', athletes.length);
      console.log('  selectedClasses:', selectedClasses);
      
      if (!selectedEvent) {
        console.log('  ⚠️ No selectedEvent, skipping');
        setResults1(new Map());
        return;
      }

      try {
        console.log('✅ Fetching results for event 1:', selectedEvent.Id, selectedEvent.Name);
        
        // Get arrangement ID and class IDs from athletes
        if (athletes.length > 0) {
          const firstAthlete = athletes[0];
          console.log('  First athlete:', firstAthlete.Utover.NavnFormatert);
          const arrangementId = firstAthlete.Arrangement?.UID;
          
          if (!arrangementId) {
            console.error('❌ No arrangement ID found');
            setResults1(new Map());
            return;
          }
          
          // Get unique class IDs from selected classes
          const classIds = new Set<number>();
          athletes.forEach(athlete => {
            if (selectedClasses.includes(athlete.Klasse.Navn)) {
              classIds.add(athlete.Klasse.UID);
            }
          });
          
          console.log('  Arrangement ID:', arrangementId);
          console.log('  Class IDs:', Array.from(classIds));
          
          // Fetch results for each class and combine
          const allResults = new Map<number, Participant>();
          
          for (const classId of Array.from(classIds)) {
            const data = await fetchResultData(selectedEvent.Id, arrangementId, classId);
            console.log(`🔍 Result data for class ${classId}:`, data);
            console.log(`🔍 All keys in result data:`, Object.keys(data));
            console.log(`🔍 data.Items:`, data.Items);
            console.log(`🔍 data.Items count:`, Object.keys(data.Items || {}).length);
            console.log(`🔍 data.CompetitionItems:`, data.CompetitionItems);
            
            // Items is an object/dictionary, not an array
            const items = data.Items || {};
            const itemsArray = Object.values(items);
            console.log(`🔍 Using items with count:`, itemsArray.length);
            
            if (itemsArray.length > 0) {
              console.log(`🔍 First item structure:`, itemsArray[0]);
              console.log(`🔍 First item keys:`, Object.keys(itemsArray[0]));
            }
            
            itemsArray.forEach(p => {
              if (p.Utover?.UID) {
                allResults.set(p.Utover.UID, p);
              }
            });
          }
          
          setResults1(allResults);
          console.log(`Loaded ${allResults.size} results from event 1`);
        }
      } catch (err) {
        console.error('Failed to load results for event 1:', err);
        setResults1(new Map());
      }
    };

    loadResults1();
  }, [selectedEvent, athletes, selectedClasses]);

  useEffect(() => {
    const loadAthletes2 = async () => {
      if (!selectedEvent2) {
        setAthletes2([]);
        return;
      }

      try {
        setLoading2(true);
        const data = await fetchStartlistData(selectedEvent2.Id);
        const participantArray = Object.values(data.Items);
        setAthletes2(participantArray);
        console.log(`Loaded ${participantArray.length} athletes from event 2`);
      } catch (err) {
        console.error('Failed to load athletes for event 2:', err);
        message.error('Failed to load athletes for event 2');
      } finally {
        setLoading2(false);
      }
    };

    loadAthletes2();
  }, [selectedEvent2]);

  // Load results for event 2
  useEffect(() => {
    const loadResults2 = async () => {
      if (!selectedEvent2) {
        setResults2(new Map());
        return;
      }

      try {
        console.log('Fetching results for event 2:', selectedEvent2.Id, selectedEvent2.Name);
        
        // Get arrangement ID and class IDs from athletes
        if (athletes2.length > 0) {
          const firstAthlete = athletes2[0];
          const arrangementId = firstAthlete.Arrangement?.UID;
          
          if (!arrangementId) {
            console.error('No arrangement ID found for event 2');
            setResults2(new Map());
            return;
          }
          
          // Get unique class IDs from selected classes
          const classIds = new Set<number>();
          athletes2.forEach(athlete => {
            if (selectedClasses2.includes(athlete.Klasse.Navn)) {
              classIds.add(athlete.Klasse.UID);
            }
          });
          
          console.log('Event 2 - Arrangement ID:', arrangementId);
          console.log('Event 2 - Class IDs:', Array.from(classIds));
          
          // Fetch results for each class and combine
          const allResults = new Map<number, Participant>();
          
          for (const classId of Array.from(classIds)) {
            const data = await fetchResultData(selectedEvent2.Id, arrangementId, classId);
            console.log(`🔍 Event 2 - Result data for class ${classId}:`, data);
            console.log(`🔍 Event 2 - All keys:`, Object.keys(data));
            console.log(`🔍 Event 2 - Items count:`, Object.keys(data.Items || {}).length);
            
            // Items is an object/dictionary, not an array
            const items = data.Items || {};
            const itemsArray = Object.values(items);
            console.log(`🔍 Event 2 - Using items with count:`, itemsArray.length);
            
            if (itemsArray.length > 0) {
              console.log(`🔍 Event 2 - First item:`, itemsArray[0]);
            }
            
            itemsArray.forEach(p => {
              if (p.Utover?.UID) {
                allResults.set(p.Utover.UID, p);
              }
            });
          }
          
          setResults2(allResults);
          console.log(`Loaded ${allResults.size} results from event 2`);
        }
      } catch (err) {
        console.error('Failed to load results for event 2:', err);
        setResults2(new Map());
      }
    };

    loadResults2();
  }, [selectedEvent2, athletes2, selectedClasses2]);

  // Persist selections to localStorage whenever they change
  useEffect(() => {
    // Skip saving during initial load
    if (isInitialLoad.current) {
      console.log('Skipping save during initial load');
      return;
    }
    
    try {
      const selections = {
        selectedClasses,
        selectedClasses2,
        selectedEvent2Id: selectedEvent2?.Id || null
      };
      console.log('Saving selections to localStorage:', selections);
      localStorage.setItem('stafettuttakSelections', JSON.stringify(selections));
    } catch (error) {
      console.error('Error saving selections:', error);
    }
  }, [selectedClasses, selectedClasses2, selectedEvent2]);

  const combinedFilteredAthletes = useMemo(() => {
    // Use a Map to avoid duplicates based on Utover.UID (athlete ID, not participant ID)
    const athleteMap = new Map<number, Participant>();
    
    // Add athletes from event 1
    athletes.forEach(athlete => athleteMap.set(athlete.Utover.UID, athlete));
    
    // Add athletes from event 2 if selected (will override if same athlete)
    if (selectedEvent2 && athletes2.length > 0) {
      athletes2.forEach(athlete => athleteMap.set(athlete.Utover.UID, athlete));
    }
    
    const allAthletes = Array.from(athleteMap.values());
    
    let filtered = allAthletes;
    
    // Apply krets filter
    if (selectedKretser.length > 0) {
      filtered = filtered.filter(athlete => {
        const kretsName = athlete.Region?.Navn;
        return kretsName && selectedKretser.includes(kretsName);
      });
    }
    
    // Apply class filters from both selectors (OR logic between the two class lists)
    const combinedClasses = [...selectedClasses, ...selectedClasses2];
    if (combinedClasses.length > 0) {
      filtered = filtered.filter(athlete => combinedClasses.includes(athlete.Klasse.Navn));
    }
    
    return filtered;
  }, [athletes, athletes2, selectedEvent, selectedEvent2, results1, results2, selectedKretser, selectedClasses, selectedClasses2]);

  const athleteColumns: ColumnsType<Participant> = [
    {
      title: 'Navn',
      key: 'name',
      render: (participant: Participant) => participant.Utover.NavnFormatert,
      sorter: (a: Participant, b: Participant) => a.Utover.NavnFormatert.localeCompare(b.Utover.NavnFormatert),
    },
    {
      title: 'Klubb',
      key: 'club',
      render: (participant: Participant) => participant.Utover.Klubb.Navn,
      sorter: (a: Participant, b: Participant) => a.Utover.Klubb.Navn.localeCompare(b.Utover.Klubb.Navn),
    },
    {
      title: 'Klasse',
      key: 'class',
      render: (participant: Participant) => participant.Klasse.Navn,
      sorter: (a: Participant, b: Participant) => a.Klasse.Navn.localeCompare(b.Klasse.Navn),
    },
    {
      title: 'Resultat Event 1',
      key: 'result1',
      render: (participant: Participant, _record: Participant, index: number) => {
        const resultParticipant = results1.get(participant.Utover.UID);
        if (resultParticipant) {
          // Debug logging for first 3 only
          if (index < 3) {
            console.log('🔍', participant.Utover.NavnFormatert);
            console.log('  All keys:', Object.keys(resultParticipant));
            if (resultParticipant.Data) {
              console.log('  Data keys:', Object.keys(resultParticipant.Data));
              console.log('  Data:', resultParticipant.Data);
            }
            if (resultParticipant.EtappeDeltaker && resultParticipant.EtappeDeltaker.length > 0) {
              console.log('  EtappeDeltaker[0] keys:', Object.keys(resultParticipant.EtappeDeltaker[0]));
              console.log('  EtappeDeltaker[0]:', resultParticipant.EtappeDeltaker[0]);
            }
            // Check for common time fields
            const timeFields = ['Tid', 'Time', 'TotalTid', 'ResultTime', 'FinishTime', 'SluttTid'];
            timeFields.forEach(field => {
              if ((resultParticipant as any)[field]) {
                console.log(`  Found ${field}:`, (resultParticipant as any)[field]);
              }
            });
          }
          return '✓';
        }
        return '-';
      },
      sorter: (a: Participant, b: Participant) => {
        const aResult = results1.get(a.Utover.UID);
        const bResult = results1.get(b.Utover.UID);
        const aStatus = aResult?.Status || '';
        const bStatus = bResult?.Status || '';
        return aStatus.localeCompare(bStatus);
      },
      width: 150,
    },
    {
      title: 'Resultat Event 2',
      key: 'result2',
      render: (participant: Participant) => {
        const resultParticipant = results2.get(participant.Utover.UID);
        if (resultParticipant) {
          return '✓';
        }
        return '-';
      },
      sorter: (a: Participant, b: Participant) => {
        const aResult = results2.get(a.Utover.UID);
        const bResult = results2.get(b.Utover.UID);
        const aStatus = aResult?.Status || '';
        const bStatus = bResult?.Status || '';
        return aStatus.localeCompare(bStatus);
      },
      width: 120,
    },
  ];

  const handleEventSelect = (eventId: number) => {
    const event = events.find(e => e.Id === eventId);
    setSelectedEvent(event || null);
    // Reset selections when event changes
    setSelectedClasses([]);
  };

  const handleEventSelect2 = (eventId: number) => {
    const event = events.find(e => e.Id === eventId);
    setSelectedEvent2(event || null);
    // Reset selections when event changes
    setSelectedClasses2([]);
  };

  const formatDate = (date: string) => {
    const eventDate = new Date(date);
    return eventDate.toLocaleDateString('no-NO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div style={{ padding: '20px' }}>
      <Card>
        <Title level={2}>Stafettuttak</Title>

        <Form layout="vertical" style={{ marginTop: '20px' }}>
          <Form.Item label="Filtrer på krets">
            <Select
              mode="multiple"
              placeholder="Velg kretser å filtrere på"
              value={selectedKretser}
              onChange={setSelectedKretser}
              style={{ width: '100%', maxWidth: '600px' }}
              size="large"
              allowClear
            >
              {kretser.map(krets => (
                <Option key={krets} value={krets}>
                  {krets}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Velg arrangement">
                {eventsLoading ? (
                  <Spin />
                ) : (
                  <Select
                    placeholder="Velg et arrangement"
                    value={selectedEvent?.Id}
                    onChange={handleEventSelect}
                    style={{ width: '100%' }}
                    size="large"
                    showSearch
                    filterOption={(input, option) =>
                      String(option?.children).toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {events.map(event => (
                      <Option key={event.Id} value={event.Id}>
                        {event.Name} - {formatDate(event.Date)}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Velg klasser (valgfritt)">
                <Select
                  mode="multiple"
                  placeholder="Velg klasser å filtrere på"
                  value={selectedClasses}
                  onChange={setSelectedClasses}
                  style={{ width: '100%' }}
                  size="large"
                  allowClear
                  disabled={!selectedEvent}
                >
                  {classes.map(className => (
                    <Option key={className} value={className}>
                      {className}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Velg arrangement 2">
                {eventsLoading ? (
                  <Spin />
                ) : (
                  <Select
                    placeholder="Velg et arrangement"
                    value={selectedEvent2?.Id}
                    onChange={handleEventSelect2}
                    style={{ width: '100%' }}
                    size="large"
                    showSearch
                    filterOption={(input, option) =>
                      String(option?.children).toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {events.map(event => (
                      <Option key={event.Id} value={event.Id}>
                        {event.Name} - {formatDate(event.Date)}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Velg klasser 2 (valgfritt)">
                <Select
                  mode="multiple"
                  placeholder="Velg klasser å filtrere på"
                  value={selectedClasses2}
                  onChange={setSelectedClasses2}
                  style={{ width: '100%' }}
                  size="large"
                  allowClear
                  disabled={!selectedEvent2}
                >
                  {classes.map(className => (
                    <Option key={className} value={className}>
                      {className}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {(selectedEvent || selectedEvent2) && (
          <div style={{ marginTop: '30px' }}>
            <Title level={4}>
              Kombinert liste av utøvere
              {loading || loading2 ? ' (Laster...)' : ''}
            </Title>
            <Space direction="vertical" size="small" style={{ marginBottom: '16px' }}>
              <Text>
                <strong>Totalt:</strong> {combinedFilteredAthletes.length} utøvere
              </Text>
              {selectedEvent && (
                <Text type="secondary">
                  • Arrangement 1: {selectedEvent.Name}
                  {selectedClasses.length > 0 && ` (${selectedClasses.length} klasse(r))`}
                </Text>
              )}
              {selectedEvent2 && (
                <Text type="secondary">
                  • Arrangement 2: {selectedEvent2.Name}
                  {selectedClasses2.length > 0 && ` (${selectedClasses2.length} klasse(r))`}
                </Text>
              )}
              {selectedKretser.length > 0 && (
                <Text type="secondary">
                  • Filtrert på krets: {selectedKretser.join(', ')}
                </Text>
              )}
            </Space>

            <Table
              columns={athleteColumns}
              dataSource={combinedFilteredAthletes}
              rowKey="UID"
              pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total) => `Totalt ${total} utøvere`,
              }}
              loading={loading || loading2}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default Stafettuttak;
